import { Definitions } from "@asosteam/asos-web-datalayer";
import { Facet } from "@repo/apis/browse-search-v2";
import { Filters } from "@repo/ui/product-filtering";

type ViewItemListState = Exclude<
  Definitions["view_item_list"],
  undefined
>["item_list_state"];

export type DetermineViewItemListStateParams = {
  pageType?: "category" | "search";
  searchTerm?: string;
  itemCount?: number;
  currentPage?: number;
  facets?: Facet[];
  filters?: Filters;
  hasAppliedFilters?: boolean;
};

/**
 * Determines the appropriate state value for view_item_list analytics
 * based on the page context and user interactions.
 *
 * Priority order:
 * 1. Pagination - always takes precedence on both category and search pages
 * 2. Sorting/Refinements - can occur on both category and search pages
 * 3. Search-specific states - only for search pages when no sorting/refinements
 * 4. Category - for category pages when no sorting/refinements
 * 5. undefined - fallback for unknown page types or missing context
 */
export function determineViewItemListState({
  pageType,
  searchTerm,
  itemCount,
  currentPage,
  facets,
  filters,
  hasAppliedFilters,
}: DetermineViewItemListStateParams): ViewItemListState {
  if (currentPage && currentPage > 1) {
    return "pagination";
  }

  const hasSort = filters?.sort && filters.sort !== "";
  const hasRefinements =
    hasAppliedFilters || checkForAppliedRefinements(facets, filters);

  if (hasSort && hasRefinements) {
    return "sort_and_refinement";
  }

  if (hasSort) {
    return "sort";
  }

  if (hasRefinements) {
    return "refinement";
  }

  if (pageType === "search") {
    if (itemCount === 0) {
      return "failed_search";
    }

    if (!searchTerm) {
      return "generic_search";
    }

    return "search";
  }

  if (pageType === "category") {
    return "category";
  }

  return undefined;
}

function checkForAppliedRefinements(
  facets?: Facet[],
  filters?: Filters,
): boolean {
  if (!facets || !filters) {
    return false;
  }

  return facets.some((facet) => {
    if (facet.id === "sort") {
      return false;
    }

    const filterValue = filters[facet.id];

    if (Array.isArray(filterValue)) {
      return filterValue.length > 0;
    }

    if (typeof filterValue === "string") {
      return filterValue !== "";
    }

    return false;
  });
}
